import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Menu, MenuItem, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import OpenInNew from "@material-ui/icons/OpenInNew";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import { AISENSY_PARTNER_ID } from "../../config/config";
import { decideCase, fetchCase, fetchWhiteLabelUrl, ownerLoginUrl, reinstate, setOwnerSettings, setWhitelist } from "./reviewApi";
import Evidence from "./Evidence";
import RestoreOutlined from "@material-ui/icons/RestoreOutlined";
import VerifiedUserOutlined from "@material-ui/icons/VerifiedUserOutlined";
import RemoveCircleOutline from "@material-ui/icons/RemoveCircleOutline";
import {
  BusinessContextSection,
  CategoryField,
  CheckRow,
  DialogSection,
  Field,
  ImpactBox,
  LEGIT_TOGGLE,
  RadioCards,
  ReviewDialog,
  SegmentedToggle,
  SelectField,
} from "./ReviewDialog";
import { AccountFacts, DeviceSection, History, OwnerProjects, TrustSection } from "./CaseSections";
import { buildWhy, caseLegitLabel, caseWorstLabel, heldCount, restorableCount, senderDomain } from "./caseText";
import {
  CONSEQUENCE,
  INDUSTRIES,
  REASON_MAX,
  REASON_MIN,
  SENDER_TYPES,
  STATUS_HELP,
  CopyText,
  ErrorLine,
  Section,
  SkeletonLines,
  StatusPill,
  TextAction,
  contextBody,
  contextFrom,
  contextReady,
  errMsg,
  fromNow,
  pal,
  plural,
  senderLabel,
  shortAge,
  shortId,
  tierColor,
  usePal,
  useUi,
} from "./reviewUi";
import { useSnackbarContext } from "../../commons/Snackbar/Snackbar";

// Right pane: one case. Header with the case-level decision, four key
// numbers, then WHY IT'S HERE, EVIDENCE, TRUST, SAME DEVICE, OTHER PROJECTS,
// ACCOUNT and HISTORY.

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  return {
    root: {
      padding: "36px 56px 64px",
      "@media (max-width: 1280px)": { padding: "32px 32px 56px" },
    },
    top: { display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" },
    title: { fontSize: 22, fontWeight: 600, letterSpacing: "-.02em", color: c.primary, lineHeight: 1.3, wordBreak: "break-word" },
    meta: { color: c.hint, fontSize: 12, marginTop: 4 },
    sender: { color: c.hint, fontSize: 12, marginTop: 2 },
    login: {
      display: "flex",
      gap: 16,
      marginTop: 6,
      "& button": { fontSize: 12, color: c.text2, "&:hover": { color: c.primary } },
      "& svg": { fontSize: 13, marginLeft: 3 },
    },
    suspended: { color: c.error, fontSize: 12.5, marginTop: 6 },
    hint: { color: c.text2, fontSize: 12, marginTop: 6, "& button": { fontSize: 12 } },
    tipBy: { display: "block", marginTop: 6 },
    about: {
      marginTop: 10,
      maxWidth: 680,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: c.text2,
      "& b": { fontWeight: 600, color: c.text },
      "& p": { margin: "2px 0 0" },
    },
    aboutBy: { color: c.hint, fontSize: 11.5, marginTop: 2 },
    acts: { marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" },
    kpis: { display: "flex", gap: "16px 48px", flexWrap: "wrap", margin: "28px 0 0" },
    kv: {
      fontSize: 28,
      fontWeight: 600,
      letterSpacing: "-.02em",
      fontVariantNumeric: "tabular-nums",
      color: c.primary,
      lineHeight: 1.4,
    },
    kl: { color: c.hint, fontSize: 12 },
    why: { "& p": { margin: "0 0 8px", fontSize: 14, lineHeight: 1.6, maxWidth: 680 }, "& b": { fontWeight: 600 } },
    stale: { color: c.error, fontSize: 12, marginTop: 8 },
  };
});

const ACTIONS = {
  dismiss: { label: "Dismiss", confirm: "Dismiss", danger: false },
  suspend: { label: "Suspend", confirm: "Suspend", danger: true },
  reinstate: { label: "Reinstate", confirm: "Reinstate", danger: false },
  sender: { label: "Mark as aggregator / service provider", confirm: "Save", danger: false },
  unmark_sender: { label: "Unmark", confirm: "Unmark", danger: false },
  whitelist: { label: "Whitelist account", confirm: "Whitelist account", danger: false },
  unwhitelist: { label: "Remove whitelist", confirm: "Remove whitelist", danger: false },
};
// Owner-level settings: apply to every project of the owner, resolve no items.
const OWNER_ACTIONS = ["sender", "unmark_sender", "whitelist", "unwhitelist"];
const PILL_STYLE = { marginLeft: 10, fontSize: 12, fontWeight: 500, letterSpacing: 0, verticalAlign: "3px" };

// "all 3 projects of this owner" / "this owner's only project"
const allProjects = (n) => (n === 1 ? "this owner's only project" : `all ${n} projects of this owner`);
// Short impact lines per owner-level dialog.
const ownerLines = (action, n, typeLabel) =>
  ({
    sender: [
      "Naming other brands won't be flagged; only real phishing, betting or illegal content is held.",
      `Applies to ${allProjects(n)}.`,
    ],
    unmark_sender: [
      `Stop treating this owner as ${/^[aeiou]/i.test(typeLabel) ? "an" : "a"} ${typeLabel}.`,
      "Templates naming other brands can be flagged again.",
      `Applies to ${allProjects(n)}.`,
    ],
    whitelist: [
      `${n === 1 ? "This owner's only project skips" : `All ${n} projects of this owner skip`} spam screening — templates are never held or listed.`,
      "Links to known scam hosts are still blocked.",
      "You can remove this any time.",
    ],
    unwhitelist: [`${n === 1 ? "This owner's only project goes" : `All ${n} projects of this owner go`} back to normal spam screening.`],
  }[action]);

// "Set by Priya · 3 days ago · Note: lends for 40 NBFCs"
const byLine = (by, at, note) =>
  [by ? `Set by ${by}` : "Set", at ? fromNow(at) : "", note ? `Note: ${note}` : ""].filter(Boolean).join(" · ");

// "Dismissed 3 items · 4 sent to Meta · 2 restored · 1 rejected by Meta" (non-zero parts only).
const dismissText = (res) => {
  const resolved = res?.resolved || 0;
  const h = res?.held || {};
  const metaRejected = (h.metaRejected || 0) + (h.restoreMetaRejected || 0);
  const gone = (h.gone || 0) + (h.restoreGone || 0);
  // All projects: "Dismissed 6 projects · 41 items · 12 sent to Meta · …"
  const all = res?.allProjects;
  const parts = [
    all ? `Dismissed ${plural(res.projects || 0, "project")}` : "",
    all ? plural(resolved, "item") : resolved ? `Dismissed ${plural(resolved, "item")}` : "",
    h.submitted ? `${h.submitted} sent to Meta` : "",
    h.restored ? `${h.restored} restored` : "",
    metaRejected ? `${metaRejected} rejected by Meta` : "",
    gone ? `${gone} no longer exist${gone === 1 ? "s" : ""}` : "",
    h.restoreFailed ? `${h.restoreFailed} couldn't be restored` : "",
    res?.strikesCleared ? `${plural(res.strikesCleared, "strike")} cleared` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : `Dismissed ${plural(0, "item")}`;
};

const successText = (action, res) => {
  const resolved = res?.resolved || 0;
  if (OWNER_ACTIONS.includes(action)) {
    const n = plural(res?.projects || 0, "project");
    if (action === "sender") return `Marked as ${senderLabel(res?.senderType, res?.industry).toLowerCase()} (${n})`;
    if (action === "unmark_sender") return `Unmarked (${n})`;
    return action === "whitelist" ? `Whitelisted (${n})` : `Whitelist removed (${n})`;
  }
  if (action === "dismiss") return dismissText(res);
  if (action === "reinstate") {
    const n = (res?.assistantIds || []).length;
    return res?.scope === "account" ? `Reinstated ${plural(n, "project")}` : "Project reinstated";
  }
  const s = res?.suspension || {};
  if (s.alreadySuspended) return `Already suspended; ${plural(resolved, "item")} resolved`;
  if (action === "suspend_account") {
    return `Account suspended: ${plural((s.assistantIds || []).length, "project")}, ${plural(s.agentsDisabled || 0, "login")} disabled`;
  }
  return "Project suspended";
};

function Loading() {
  const s = useStyles();
  return (
    <Box className={s.root}>
      <Skeleton width={260} height={34} />
      <Skeleton width={420} height={18} />
      <Box className={s.kpis}>
        {[0, 1, 2, 3].map((i) => (
          <Box key={i}>
            <Skeleton width={48} height={40} />
            <Skeleton width={90} height={16} />
          </Box>
        ))}
      </Box>
      <Box style={{ marginTop: 40, maxWidth: 680 }}>
        <SkeletonLines lines={3} widths={["90%", "75%", "82%"]} />
      </Box>
      <Box style={{ marginTop: 40, maxWidth: 680 }}>
        <SkeletonLines lines={5} />
      </Box>
    </Box>
  );
}

// Owner login links (Assistants "login" action). mode: owner | whiteLabel | copy.
function OwnerLogin({ clientId, assistantId, partnerId }) {
  const { showSnackbar } = useSnackbarContext() || {};
  const [busy, setBusy] = useState(false);
  const whiteLabel = !!partnerId && partnerId !== AISENSY_PARTNER_ID;
  const go = async (mode) => {
    setBusy(true);
    try {
      const base = mode === "whiteLabel" ? await fetchWhiteLabelUrl(partnerId) : "";
      const url = await ownerLoginUrl(clientId, assistantId, base);
      if (mode === "copy") {
        await navigator.clipboard.writeText(url);
        showSnackbar && showSnackbar("Login URL copied", "success");
      } else {
        window.open(url, "_blank");
      }
    } catch (e) {
      showSnackbar && showSnackbar(errMsg(e, "Couldn't log in as the owner"), "error");
    } finally {
      setBusy(false);
    }
  };
  if (!clientId) return null;
  return (
    <>
      <TextAction inline disabled={busy} onClick={() => go("owner")}>
        Owner login
        <OpenInNew />
      </TextAction>
      {whiteLabel ? (
        <TextAction inline disabled={busy} onClick={() => go("whiteLabel")}>
          Owner login (white label)
          <OpenInNew />
        </TextAction>
      ) : null}
      <TextAction inline disabled={busy} onClick={() => go("copy")}>
        Copy login URL
      </TextAction>
    </>
  );
}

export default function CaseView({ assistantId, canWrite, canLogin, onOpenCase, onChanged }) {
  const s = useStyles();
  const ui = useUi();
  const c = usePal();
  const { showSnackbar } = useSnackbarContext() || {};
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [label, setLabel] = useState("");
  const [restore, setRestore] = useState(false);
  // Dismiss: why the checker got it wrong (sent as the decision note, required).
  const [reason, setReason] = useState("");
  // Optional note for every other dialog.
  const [note, setNote] = useState("");
  // Suspend: all of the owner's projects (suspend_account) or just this one.
  const [suspendAll, setSuspendAll] = useState(true);
  // Dismiss: every project of the owner the same way. Off by default (owner's call).
  const [dismissAll, setDismissAll] = useState(false);
  // Suspend: also disable all agents of the account. On by default.
  const [disableAgents, setDisableAgents] = useState(true);
  const [menuAt, setMenuAt] = useState(null);
  // Owner settings form (Mark as aggregator / service provider).
  const [senderType, setSenderType] = useState("");
  const [industry, setIndustry] = useState("");
  // Business context, asked on every Dismiss (required until the owner has saved context).
  const [bizCtx, setBizCtx] = useState(() => contextFrom(null));
  // Route returned 404 (older backend): hide that owner action.
  const [unsupported, setUnsupported] = useState({});
  const reqRef = useRef(0);

  const load = useCallback(
    async (quiet) => {
      const req = ++reqRef.current;
      if (!quiet) setLoading(true);
      setError("");
      try {
        const data = await fetchCase(assistantId);
        if (req === reqRef.current) setDossier(data);
      } catch (e) {
        if (req === reqRef.current) setError(errMsg(e, "Couldn't load this account"));
      } finally {
        if (req === reqRef.current) setLoading(false);
      }
    },
    [assistantId]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const refresh = useCallback(() => {
    load(true);
    onChanged();
  }, [load, onChanged]);

  if (loading && !dossier) return <Loading />;
  if (error && !dossier) {
    return (
      <Box className={s.root}>
        <ErrorLine message={error} onRetry={() => load(false)} />
      </Box>
    );
  }
  if (!dossier) return null;

  const d = dossier;
  const suspension = d.suspension;
  const scope = suspension?.scope || "project";
  const openCount = d.openCount || 0;
  // The project's current score (same as the inbox); the latest screening's breakdown as fallback.
  const trust = d.trust || d.trustBreakdown;
  const maxAccounts = (d.devices || []).reduce((m, x) => Math.max(m, x.accountCount || 0), 0);
  const ownerCreated = d.owner?.createdOn || d.project?.createdAt;
  const why = buildWhy(d);
  const sender = senderDomain(d);
  const heldN = heldCount(d);
  // Held then rejected here earlier; Dismiss can re-submit them to Meta.
  const restorableN = restorableCount(d);
  // Restore all (default on): held templates and those rejected earlier go to Meta.
  const allOn = pending === "dismiss" && dismissAll;
  const restoreOn = pending === "dismiss" && restore && (allOn || heldN + restorableN > 0);
  const suspendAction = suspendAll ? "suspend_account" : "suspend_project";
  const savedContext = d.owner?.businessContext;
  // Owner-level settings (apply to all of the owner's projects).
  const owner = d.owner || {};
  const marked = SENDER_TYPES.some((t) => t.value === owner.senderType);
  const markedLabel = marked ? senderLabel(owner.senderType, owner.industry) : "";
  const typeWord = marked ? STATUS_HELP[owner.senderType].label.toLowerCase() : "";
  const ownerProjects = 1 + (d.siblingProjects || []).length;
  const canSender = canWrite && !unsupported.sender;
  const canWhitelist = canWrite && !unsupported.whitelist;
  const env = trust?.environment && trust.environment !== "UNKNOWN" ? ` · ${trust.environment.toLowerCase()} network` : "";

  const run = async () => {
    const trimmed = note.trim();
    setBusy(true);
    setActionError("");
    try {
      const res =
        pending === "reinstate"
          ? await reinstate(assistantId, scope)
          : pending === "sender" || pending === "unmark_sender"
          ? await setOwnerSettings(assistantId, pending === "sender" ? senderType : null, pending === "sender" ? industry : null, trimmed)
          : pending === "whitelist" || pending === "unwhitelist"
          ? await setWhitelist(assistantId, pending === "whitelist", trimmed)
          : pending === "dismiss"
          ? await decideCase(assistantId, "dismiss", reason.trim(), label, restore, contextBody(bizCtx), undefined, dismissAll)
          : await decideCase(assistantId, suspendAction, trimmed, label, false, undefined, disableAgents);
      const shown = pending === "sender" ? { senderType, industry, ...res } : res;
      const done = pending === "suspend" ? suspendAction : pending;
      showSnackbar && showSnackbar(successText(done, shown), res?.held?.restoreFailed ? "warning" : "success");
      setPending("");
      refresh();
    } catch (e) {
      if (OWNER_ACTIONS.includes(pending) && e?.response?.status === 404) {
        setUnsupported((u) => ({ ...u, [pending.includes("whitelist") ? "whitelist" : "sender"]: true }));
        setPending("");
        showSnackbar && showSnackbar("This setting isn't available yet", "error");
        return;
      }
      const metaDown = (heldN || restoreOn || allOn) && pending === "dismiss" && e?.response?.status === 502;
      const failedN = e?.response?.data?.failedProjects || e?.response?.data?.projects || ownerProjects;
      setActionError(
        metaDown && allOn
          ? `Some held templates on ${plural(failedN, "project")} couldn't be sent to Meta — try again`
          : metaDown
          ? restoreOn
            ? "Couldn't reach Meta — some held or restored templates weren't sent; try again"
            : "Couldn't reach Meta — try again"
          : errMsg(e, "The action failed")
      );
      // Meta down: the rest was applied, so show the new state.
      if (metaDown) refresh();
      else if (e?.response?.status === 409) load(true);
    } finally {
      setBusy(false);
    }
  };
  const worstLabel = caseWorstLabel(d);
  // type: preselected sender type for "sender" (Mark as aggregator / service provider).
  const ask = (key, type) => {
    setMenuAt(null);
    setActionError("");
    setLabel(key === "dismiss" ? caseLegitLabel(d) : key === "suspend" ? worstLabel : "");
    // Restore all and Suspend all projects are on by default; the reviewer can untick them.
    setRestore(key === "dismiss");
    setSuspendAll(true);
    setNote("");
    setDisableAgents(true);
    if (key === "dismiss") {
      setDismissAll(false);
      setBizCtx(contextFrom(d.owner?.businessContext));
      setReason("");
    }
    if (key === "sender") {
      setSenderType(type || (marked ? owner.senderType : "aggregator"));
      setIndustry(marked ? owner.industry || "" : "");
    }
    setPending(key);
  };
  // "2 held, 1 rejected earlier"
  const restoreCounts = [heldN ? `${heldN} held` : "", restorableN ? `${restorableN} rejected earlier` : ""].filter(Boolean).join(", ");
  const projectName = d.assistantName || "this project";
  const dialogTitle = {
    dismiss: `Dismiss ${projectName}`,
    suspend: `Suspend ${projectName}`,
    reinstate: `Reinstate ${projectName}`,
    sender: marked ? `Edit ${typeWord}` : "Mark as aggregator / service provider",
    unmark_sender: `Unmark ${typeWord}`,
    whitelist: "Whitelist account",
    unwhitelist: "Remove whitelist",
  }[pending];
  const dialogSubtitle = [
    pending === "dismiss" || pending === "suspend" || pending === "reinstate" ? owner.name : projectName,
    owner.email,
    sender.domain,
  ]
    .filter(Boolean)
    .join(" · ");
  const confirmLabel = allOn
    ? restoreOn
      ? "Dismiss all & restore"
      : "Dismiss all projects"
    : restoreOn
    ? "Dismiss & restore"
    : pending === "sender" && marked
    ? "Save"
    : pending === "suspend"
    ? suspendAll
      ? "Suspend all projects"
      : "Suspend project"
    : pending
    ? ACTIONS[pending].confirm
    : "";
  // Nothing open and restore unticked: nothing to do. Owner form: type and industry required.
  const confirmDisabled =
    (pending === "dismiss" && !allOn && !openCount && !restoreOn) ||
    (pending === "dismiss" && reason.trim().length < REASON_MIN) ||
    (pending === "dismiss" && !contextReady(bizCtx, savedContext)) ||
    (pending === "sender" && (!senderType || !industry));

  const metaParts = [
    d.owner?.name,
    d.owner?.email,
    d.project?.createdAt ? `created ${fromNow(d.project.createdAt)}` : null,
  ].filter(Boolean);

  return (
    <Box className={s.root}>
      <div className={s.top}>
        <div style={{ minWidth: 0, flex: "1 1 320px" }}>
          <div className={s.title}>
            {d.assistantName || "Unnamed project"}
            {marked ? (
              <StatusPill
                style={PILL_STYLE}
                status={{
                  label: markedLabel,
                  tone: "sender",
                  help: (
                    <>
                      {STATUS_HELP[owner.senderType].help}
                      <span className={s.tipBy}>{byLine(owner.senderTypeBy, owner.senderTypeAt, owner.senderTypeNote)}</span>
                    </>
                  ),
                  term: [`senderType=${owner.senderType}`, owner.industry ? `industry=${owner.industry}` : ""].filter(Boolean).join(" · "),
                }}
              />
            ) : null}
            {owner.whitelisted ? (
              <StatusPill
                style={PILL_STYLE}
                status={{
                  ...STATUS_HELP.whitelisted,
                  tone: "whitelisted",
                  help: (
                    <>
                      {STATUS_HELP.whitelisted.help}
                      <span className={s.tipBy}>{byLine(owner.whitelistedBy, owner.whitelistedAt, owner.whitelistNote)}</span>
                    </>
                  ),
                }}
              />
            ) : null}
          </div>
          <div className={s.meta}>
            {metaParts.map((p) => `${p} · `).join("")}
            <CopyText value={assistantId} label="Copy project id">
              {shortId(assistantId)}
            </CopyText>
          </div>
          {sender.domain || sender.ownLinks.length ? (
            <div className={s.sender}>
              {sender.domain ? `Sender domain ${sender.domain}` : ""}
              {sender.domain && sender.ownLinks.length ? " · " : ""}
              {sender.ownLinks.length
                ? `${sender.domain ? "links go" : "Links go"} to the sender's own domain (${sender.ownLinks.join(", ")})`
                : ""}
            </div>
          ) : null}
          {!marked && d.aggregatorHint?.reason ? (
            <div className={s.hint}>
              Looks like an aggregator: {d.aggregatorHint.reason}
              {canSender ? (
                <>
                  {" — "}
                  <TextAction inline tone="primary" disabled={busy} onClick={() => ask("sender", "aggregator")}>
                    Mark as aggregator
                  </TextAction>
                </>
              ) : null}
            </div>
          ) : null}
          {canLogin && d.clientId ? (
            <div className={s.login}>
              <OwnerLogin clientId={d.clientId} assistantId={assistantId} partnerId={d.project?.partnerId} />
            </div>
          ) : null}
          {savedContext && (savedContext.about || savedContext.useCases) ? (
            <div className={s.about}>
              <b>About this business</b>
              {savedContext.about ? <p>{savedContext.about}</p> : null}
              {savedContext.useCases ? <p>Uses WhatsApp for: {savedContext.useCases}</p> : null}
              {savedContext.by || savedContext.at ? (
                <div className={s.aboutBy}>
                  {[savedContext.by ? `Saved by ${savedContext.by}` : "Saved", savedContext.at ? fromNow(savedContext.at) : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              ) : null}
            </div>
          ) : null}
          {suspension ? (
            <div className={s.suspended}>
              Suspended ({scope}) by {suspension.by || "—"} · {fromNow(suspension.at)}
              {suspension.note ? ` · ${suspension.note}` : ""}
            </div>
          ) : null}
        </div>
        {canWrite ? (
          <div className={s.acts}>
            <Button disableElevation className={ui.filledPrimary} disabled={busy} onClick={() => ask("dismiss")}>
              Dismiss
            </Button>
            {suspension ? (
              <Button disableElevation className={ui.filledPrimary} disabled={busy} onClick={() => ask("reinstate")}>
                Reinstate
              </Button>
            ) : (
              <Button disableElevation className={ui.filledDanger} disabled={busy} onClick={() => ask("suspend")}>
                Suspend
              </Button>
            )}
            {canSender || canWhitelist ? (
              <>
                <TextAction
                  disabled={busy}
                  aria-haspopup="menu"
                  aria-expanded={menuAt ? "true" : undefined}
                  onClick={(e) => setMenuAt(e.currentTarget)}
                  endIcon={<ArrowDropDown />}
                >
                  Actions
                </TextAction>
                <Menu
                  anchorEl={menuAt}
                  open={!!menuAt}
                  onClose={() => setMenuAt(null)}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {canWhitelist ? (
                    <MenuItem dense onClick={() => ask(owner.whitelisted ? "unwhitelist" : "whitelist")}>
                      {owner.whitelisted ? "Remove whitelist" : "Whitelist account"}
                    </MenuItem>
                  ) : null}
                  {canSender && marked ? (
                    <MenuItem dense onClick={() => ask("sender")}>
                      Edit {typeWord}
                    </MenuItem>
                  ) : null}
                  {canSender && marked ? (
                    <MenuItem dense onClick={() => ask("unmark_sender")}>
                      Unmark {typeWord}
                    </MenuItem>
                  ) : null}
                  {canSender && !marked ? (
                    <MenuItem dense onClick={() => ask("sender", "aggregator")}>
                      Mark as aggregator
                    </MenuItem>
                  ) : null}
                  {canSender && !marked ? (
                    <MenuItem dense onClick={() => ask("sender", "service_provider")}>
                      Mark as service provider
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {pending ? (
        <ReviewDialog
          key={pending}
          title={dialogTitle}
          subtitle={dialogSubtitle}
          size={["dismiss", "suspend", "sender"].includes(pending) ? "sm" : "xs"}
          danger={ACTIONS[pending].danger}
          confirmLabel={confirmLabel}
          busy={busy}
          confirmDisabled={confirmDisabled}
          error={actionError}
          onConfirm={run}
          onCancel={() => {
            setPending("");
            setActionError("");
          }}
        >
          {pending === "dismiss" ? (
            <>
              <DialogSection label="What happens">
                <ImpactBox
                  lines={
                    allOn
                      ? [
                          `Open items on all ${ownerProjects} projects marked OK`,
                          restoreOn ? "Held templates on all projects sent to Meta" : "",
                          restoreOn ? "Rejected templates on all projects restored and sent to Meta" : "",
                          "Nothing is suspended",
                        ]
                      : [
                          openCount ? `${plural(openCount, "open item")} marked OK` : "Nothing is open on this project",
                          restoreOn && heldN ? `${plural(heldN, "held template")} sent to Meta` : "",
                          restoreOn && restorableN ? `${plural(restorableN, "rejected template")} restored and sent to Meta` : "",
                          "Nothing is suspended",
                        ]
                  }
                >
                  <CheckRow
                    checked={restore}
                    onChange={setRestore}
                    disabled={busy}
                    label={STATUS_HELP.restore.label}
                    secondary={
                      allOn
                        ? "Held and rejected templates on all projects — sent to Meta as written"
                        : restoreCounts
                        ? `${restoreCounts} — sent to Meta as written`
                        : "Nothing held or rejected on this project"
                    }
                    help={STATUS_HELP.restore.help}
                    term={STATUS_HELP.restore.term}
                  />
                  {ownerProjects > 1 ? (
                    <div style={{ marginTop: 12 }}>
                      <CheckRow
                        checked={dismissAll}
                        onChange={setDismissAll}
                        disabled={busy}
                        label={`Dismiss all projects of this owner (${ownerProjects})`}
                        secondary="Every project of this owner with open or restorable items is dismissed the same way, with this reason and business details."
                      />
                    </div>
                  ) : null}
                </ImpactBox>
              </DialogSection>
              <DialogSection label="Correct label">
                <SegmentedToggle options={LEGIT_TOGGLE} value={label} onChange={setLabel} disabled={busy} ariaLabel="Correct label" />
              </DialogSection>
              <BusinessContextSection value={bizCtx} onChange={setBizCtx} disabled={busy} />
              <DialogSection label="Reviewer note">
                <Field
                  label="Why was this wrongly classified?"
                  placeholder="e.g. Loan marketplace sending partner-lender offers; not impersonation"
                  value={reason}
                  onChange={setReason}
                  min={REASON_MIN}
                  max={REASON_MAX}
                  disabled={busy}
                  required
                />
              </DialogSection>
            </>
          ) : null}
          {pending === "suspend" ? (
            <>
              <DialogSection label="What happens">
                <ImpactBox
                  tone="danger"
                  lines={[
                    suspendAll
                      ? ownerProjects === 1
                        ? "This owner's only project is suspended"
                        : `All ${ownerProjects} projects of this owner are suspended`
                      : "This project is suspended",
                    "No new templates or messages until reinstated",
                    disableAgents ? "All agents of this account are disabled (owner and every team member)" : "Agents stay active",
                    heldN ? `${plural(heldN, "held template")} will be rejected` : "",
                  ]}
                />
              </DialogSection>
              <DialogSection label="Scope">
                <CheckRow
                  bordered
                  checked={suspendAll}
                  onChange={setSuspendAll}
                  disabled={busy}
                  label="Suspend all projects of this owner"
                  secondary={ownerProjects > 1 ? `All ${ownerProjects} projects, not just ${projectName}.` : "This owner has no other project."}
                />
                <CheckRow
                  bordered
                  checked={disableAgents}
                  onChange={setDisableAgents}
                  disabled={busy}
                  label="Disable all agents of this account"
                  secondary="The owner and every team member are disabled until reinstated."
                />
              </DialogSection>
              <DialogSection label="Details">
                <CategoryField value={label} onChange={setLabel} modelLabel={worstLabel} disabled={busy} />
                <Field label="Note (optional)" value={note} onChange={setNote} max={500} disabled={busy} single />
              </DialogSection>
            </>
          ) : null}
          {pending === "sender" ? (
            <>
              <DialogSection label="Type">
                <RadioCards options={SENDER_TYPES} value={senderType} onChange={setSenderType} disabled={busy} ariaLabel="Type" />
              </DialogSection>
              <DialogSection label="Details">
                <SelectField
                  label="Industry"
                  value={industry}
                  onChange={setIndustry}
                  options={INDUSTRIES}
                  placeholder={{ label: "Choose an industry", disabled: true }}
                  disabled={busy}
                />
                <Field label="Note (optional)" value={note} onChange={setNote} max={500} disabled={busy} single />
              </DialogSection>
              <DialogSection label="What changes">
                <ImpactBox lines={ownerLines("sender", ownerProjects, typeWord)} />
              </DialogSection>
            </>
          ) : null}
          {["unmark_sender", "whitelist", "unwhitelist"].includes(pending) ? (
            <>
              <ImpactBox
                icon={pending === "unmark_sender" ? RemoveCircleOutline : VerifiedUserOutlined}
                lines={ownerLines(pending, ownerProjects, typeWord)}
              />
              <DialogSection>
                <div style={{ marginTop: 16 }}>
                  <Field label="Note (optional)" value={note} onChange={setNote} max={500} disabled={busy} single />
                </div>
              </DialogSection>
            </>
          ) : null}
          {pending === "reinstate" ? <ImpactBox icon={RestoreOutlined} lines={[CONSEQUENCE.reinstate]} /> : null}
        </ReviewDialog>
      ) : null}
      {error ? <div className={s.stale}>Couldn't refresh: {error}</div> : null}

      <div className={s.kpis}>
        <div>
          <div className={s.kv} style={{ color: trust ? tierColor(c, trust.tier) : c.hint }}>
            {trust?.score ?? "—"}
          </div>
          <div className={s.kl}>trust{env}</div>
        </div>
        <div>
          <div className={s.kv}>{openCount}</div>
          <div className={s.kl}>open item{openCount === 1 ? "" : "s"}</div>
        </div>
        <div>
          <div className={s.kv}>{maxAccounts || "—"}</div>
          <div className={s.kl}>accounts on device</div>
        </div>
        <div>
          <div className={s.kv}>{shortAge(ownerCreated)}</div>
          <div className={s.kl}>account age</div>
        </div>
      </div>

      {why.length ? (
        <Section title="Why it's here">
          <div className={s.why}>
            {why.map((parts, i) => (
              <p key={i}>
                {parts.map((p, j) => (typeof p === "string" ? <React.Fragment key={j}>{p}</React.Fragment> : <b key={j}>{p.b}</b>))}
              </p>
            ))}
          </div>
        </Section>
      ) : null}

      <Evidence
        key={`ev-${assistantId}`}
        subjects={d.subjects}
        canWrite={canWrite}
        forcedReview={d.trustBreakdown?.forcedReview}
        businessContext={d.owner?.businessContext}
        onChanged={refresh}
      />
      <TrustSection breakdown={d.trustBreakdown} trust={d.trust} />
      <DeviceSection devices={d.devices} canWrite={canWrite} onOpenCase={onOpenCase} onChanged={refresh} />
      <OwnerProjects projects={d.siblingProjects} onOpenCase={onOpenCase} />
      <AccountFacts project={d.project} owner={d.owner} clientId={d.clientId} />
      <History dossier={d} />
    </Box>
  );
}
